import { useEffect, useState } from "react";
import { tr } from "@/i18n";
import { useLocation } from "react-router-dom";
import { useUrlTab } from "@/lib/useUrlTab";
import { Settings as GearIcon, Plus, Trash2, Upload, Download, ChevronDown, ChevronRight, Edit2, User, X, Archive, Wrench, ClipboardCheck, Factory, UserCheck, Receipt, Building2, MapPin, Star, Mail, Send, BookOpen, Cloud, Truck, Bell, Users as UsersIcon, ScrollText, CreditCard, Save, Loader2 } from "lucide-react";
import { btn } from "@/components/common/btn";
import { invalidateOnlinePayments } from "@/lib/onlinePayments";
import EmailAutomationSwitches from "@/components/settings/EmailAutomationSwitches";
import DotDigestEmailSettings from "@/components/settings/DotDigestEmailSettings";
import { ConnectOutlookButton } from "@/components/common/ConnectOutlookButton";
import { getSettings, updateSettings, getServiceTypes, createServiceType, deleteServiceType, getAccountManagers, createAccountManager, updateAccountManager, deleteAccountManager, geocodeAddress, getARReminderSettings, updateARReminderSettings, testSendARReminder, getEmailStats, setEmailAutomation, sendTestEmail, getClients } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import Equipment from "@/pages/Equipment";
import Inspections from "@/pages/Inspections";
import Manufacturers from "@/pages/Manufacturers";
import UsersPanel from "@/components/users/UsersPanel";
import GeocodingPanel from "@/components/settings/GeocodingPanel";
import DispatchCategoriesPanel from "@/components/settings/DispatchCategoriesPanel";
import SalesFollowupSettings from "@/components/settings/SalesFollowupSettings";
import { LienDigestSettings } from "@/components/settings/LienDigestSettings";
import WikiSection from "@/pages/WikiSection";
import IntegrationsTab, { INTEGRATION_SECTIONS } from "@/components/settings/IntegrationsTab";
import PushNotificationsPanel from "@/components/settings/PushNotificationsPanel";
import DispatchEmailPanel from "@/components/settings/DispatchEmailPanel";
import FieldReadinessPanel from "@/components/settings/FieldReadinessPanel";
import AuditLogPanel from "@/components/settings/AuditLogPanel";
import FoundationSlaBadge, { useFoundationSla } from "@/components/integrations/FoundationSlaBadge";
import { SettingsPanel, PanelHeader, SubTabs, Callout, EmptyState } from "@/components/settings/SettingsUI";
import { useAuth } from "@/auth/AuthContext";

// General tab sub-tabs (iter266) — same layout as the Integrations tab.
const GENERAL_SECTIONS = [
  { id: "defaults", label: "Default Rates", keywords: ["Labor cost", "Margin", "Rates"] },
  { id: "freight", label: "Freight Table", keywords: ["Freight Estimate Table"] },
  { id: "tax", label: "Tax Decision Tree", keywords: ["Tax Decision Tree PDF", "Sales tax"] },
  { id: "services", label: "Service Types" },
  { id: "arReminders", label: "Email", keywords: ["AR reminders", "Invoice reminders"] },
  { id: "payments", label: "Payments" },
  { id: "shop", label: "Timekeeping", keywords: ["Shop location", "Geofence", "GPS"] },
  // iter297 — moved here from Settings → Integrations; last tab on the right.
  { id: "push", label: "Push Notifications", keywords: ["Notifications"] },
];

export default function SettingsPage() {
  const location = useLocation();
  const { can, isNamedAdmin } = useAuth();
  // iter295 — the "past SLA" pointer lives here now (it used to be a dot
  // on the sidebar gear). Polled once, lit on the Integrations tab and
  // again on the Foundation Approval Queue sub-tab.
  const { staleCount: slaCount, title: slaTitle } = useFoundationSla(can("integrations.foundation_queue", "read"));
  const [activeTab, setActiveTab] = useUrlTab("tab", "general");
  const [integrationsSection, setIntegrationsSection] = useState(location.state?.expand || null);
  const [generalSection, setGeneralSection] = useState("defaults");
  const [settings, setSettings] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [newServiceType, setNewServiceType] = useState("");
  const [accountManagers, setAccountManagers] = useState([]);
  // Customer tax overrides (Tax Decision Tree → step 0) need the roster.
  const [clientOptions, setClientOptions] = useState([]);
  const [newOverrideClient, setNewOverrideClient] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ shop: false, tax: false, defaults: true, services: false, technicians: false, accountManagers: false, projectManagers: false, arReminders: false, dataManagement: false, foundationApproval: false, foundationBridgeOps: false, dataCleanup: false });
  // Cross-tab navigation: Equipment -> Manufacturers -> Equipment
  const [mfrAutoAdd, setMfrAutoAdd] = useState(false);
  const [pendingPartReturn, setPendingPartReturn] = useState(null);

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
      window.history.replaceState({}, document.title);
    }
    // iter160 — Foundation SLA badge passes {expand: "foundationApproval"}
    // so the Foundation queue auto-opens when a manager clicks the
    // red dot. Generic enough to support future deep-link openers.
    if (location.state?.expand) {
      // iter265 — the Foundation / Miter / data panels now live in the
      // Integrations tab, so a deep-link to one of those keys opens that
      // tab on the matching sub-tab instead of expanding a General section.
      if (INTEGRATION_SECTIONS[location.state.expand]) {
        setActiveTab("integrations");
        setIntegrationsSection(location.state.expand);
      } else {
        setGeneralSection(location.state.expand);
        setExpandedSections((prev) => ({ ...prev, [location.state.expand]: true }));
      }
    }
  }, [location.state]);

  const loadAll = async () => {
    try {
      // iter268 — the AM roster still feeds the "Signed in as" card, but
      // the roster + technician editors now live under Settings → Users.
      const [settingsRes, stRes, amRes] = await Promise.all([
        getSettings(), getServiceTypes(), getAccountManagers(),
      ]);
      setSettings(settingsRes.data);
      setServiceTypes(stRes.data);
      setAccountManagers(amRes.data);
      getClients()
        .then(r => setClientOptions((Array.isArray(r.data) ? r.data : r.data?.clients || []).map(c => ({ id: c.id, name: c.name }))))
        .catch(() => {});
    } catch { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  };

  const saveSettings = async (updates) => {
    setSaving(true);
    try {
      const res = await updateSettings({ ...settings, ...updates });
      setSettings(res.data);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const toggle = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // General sub-tabs: selecting one expands its section so nothing is
  // hidden behind a second click (matches the Integrations tab).
  const openGeneralSection = (key) => {
    setGeneralSection(key);
    setExpandedSections(prev => ({ ...prev, [key]: true }));
  };

  const updateTaxField = (category, key, field, value) => {
    const taxRates = { ...settings.tax_rates };
    taxRates[category] = { ...taxRates[category] };
    taxRates[category][key] = { ...taxRates[category][key], [field]: value };
    setSettings(prev => ({ ...prev, tax_rates: taxRates }));
  };

  const addState = () => {
    const abbr = prompt("Enter state abbreviation (e.g. TX):");
    if (!abbr) return;
    const taxRates = { ...settings.tax_rates };
    taxRates.states = { ...taxRates.states, [abbr.toUpperCase()]: { rate: 0, exempt_allowed: true, tax_no: "" } };
    setSettings(prev => ({ ...prev, tax_rates: taxRates }));
  };

  const addCity = () => {
    const name = prompt("Enter city name:");
    if (!name) return;
    const taxRates = { ...settings.tax_rates };
    taxRates.cities = { ...taxRates.cities, [name.trim()]: { state: "CO", county: "", rate: 0, never_exempt: false, tax_no: "" } };
    setSettings(prev => ({ ...prev, tax_rates: taxRates }));
  };

  const addCounty = () => {
    const name = prompt("Enter county name:");
    if (!name) return;
    const taxRates = { ...settings.tax_rates };
    taxRates.counties = { ...taxRates.counties, [name.trim()]: { state: "CO", rate: 0, type: "", tax_no: "" } };
    setSettings(prev => ({ ...prev, tax_rates: taxRates }));
  };

  const removeEntry = (category, key) => {
    const taxRates = { ...settings.tax_rates };
    taxRates[category] = { ...taxRates[category] };
    delete taxRates[category][key];
    setSettings(prev => ({ ...prev, tax_rates: taxRates }));
  };

  // ---- Customer tax overrides (step 0 of the decision tree) ----
  const customerOverrides = (settings?.tax_rates?.customer_overrides) || [];

  const setOverrides = (list) => setSettings(prev => ({
    ...prev, tax_rates: { ...(prev.tax_rates || {}), customer_overrides: list },
  }));

  const addCustomerOverride = () => {
    const client = clientOptions.find(c => c.id === newOverrideClient);
    if (!client) { toast.error("Pick a customer first"); return; }
    if (customerOverrides.some(o => o.client_id === client.id)) { toast.error("That customer already has an override"); return; }
    setOverrides([...customerOverrides, { client_id: client.id, client_name: client.name, rate: 0, tax_no: "", label: "" }]);
    setNewOverrideClient("");
  };

  const updateCustomerOverride = (clientId, field, value) => setOverrides(
    customerOverrides.map(o => o.client_id === clientId ? { ...o, [field]: value } : o));

  const removeCustomerOverride = (clientId) => setOverrides(
    customerOverrides.filter(o => o.client_id !== clientId));

  const handleAddServiceType = async () => {
    if (!newServiceType.trim()) return;
    try {
      await createServiceType({ name: newServiceType.trim() });
      setNewServiceType("");
      const res = await getServiceTypes();
      setServiceTypes(res.data);
      toast.success("Service type added");
    } catch { toast.error("Service type already exists"); }
  };

  const handleDeleteServiceType = async (id) => {
    try {
      await deleteServiceType(id);
      setServiceTypes(prev => prev.filter(s => s.id !== id));
      toast.success("Service type removed");
    } catch { toast.error("Failed to delete"); }
  };









  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div></div>;
  if (!settings) return <div className="text-center py-12 text-slate-500">No settings found</div>;

  const taxRates = settings.tax_rates || {};

  // Grouped: Setup · Catalogs · Ops — `startsGroup` draws the divider.
  // Each tab declares its access-matrix surface (iter268) so a role only
  // sees the sub-tabs an admin granted it.
  // Order per user (iter307): General · Equipment · Inspections · Dispatch ·
  // Vendors · Users · Integrations · Wiki (· Audit Log). Geocoding lives
  // inside Dispatch as a collapsible section.
  const ALL_TABS = [
    { id: "general", label: "General", icon: GearIcon, surface: "settings.general" },
    { id: "equipment", label: "Equipment", icon: Wrench, surface: "settings.equipment" },
    { id: "inspections", label: "Inspections", icon: ClipboardCheck, surface: "settings.inspections" },
    { id: "dispatchCategories", label: "Dispatch", icon: Truck, surface: "settings.dispatch" },
    { id: "users", label: "Users", icon: UsersIcon, surface: "settings.users" },
    { id: "manufacturers", label: "Vendors", icon: Factory, surface: "settings.vendors" },
    { id: "integrations", label: "Integrations", icon: Cloud, surface: "integrations.miter" },
    { id: "wiki", label: "Wiki", icon: BookOpen, surface: "settings.wiki" },
    { id: "auditLog", label: "Audit Log", icon: ScrollText, surface: "settings.audit_log", namedAdminOnly: true },
  ];
  const TABS = ALL_TABS.filter(t => can(t.surface, "read") && (!t.namedAdminOnly || isNamedAdmin));
  const visibleTab = TABS.some(t => t.id === activeTab) ? activeTab : (TABS[0]?.id || null);
  if (TABS.length === 0) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="settings-no-access">
        <SettingsPanel>
          <EmptyState title={tr("No settings access")} hint="Ask an admin to grant you a Settings area in Users → Permissions." />
        </SettingsPanel>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-w-6xl mx-auto w-full" data-testid="settings-page">
      {/* Profile — your email used to auto-fill AM on new proposals & WOs */}
      <ProfileCard accountManagers={accountManagers} />

      {/* Tab Navigation — grouped (Setup · Catalogs · Ops) with a hairline
          between groups so nine tabs read as three clusters. */}
      <div className="flex items-center gap-1 border-b border-slate-200 px-1 flex-wrap" data-testid="settings-tabs">
        {TABS.map((tab) => {
          return (
            <div key={tab.id} className="flex items-center">
              {tab.startsGroup && <span className="w-px h-5 bg-slate-200 mx-2" aria-hidden="true" />}
              <button
                onClick={() => setActiveTab(tab.id)}
                className={`-mb-px flex items-center gap-1.5 px-3.5 py-2.5 text-sm border-b-2 transition-colors ${
                  visibleTab === tab.id
                    ? "border-brand text-brand font-semibold"
                    : "border-transparent text-slate-400 hover:text-slate-600 font-medium"
                }`}
                data-testid={`settings-tab-${tab.id}`}
              >
                {tr(tab.label)}
                {tab.id === "integrations" && (
                  <FoundationSlaBadge count={slaCount} title={slaTitle} testId="settings-tab-sla-badge" />
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Tab Content */}
      {visibleTab === "manufacturers" && <Manufacturers autoAddMode={mfrAutoAdd} onManufacturerAdded={(mfr) => {
        setMfrAutoAdd(false);
        if (pendingPartReturn) {
          setActiveTab("equipment");
          setPendingPartReturn(null);
        }
      }} />}
      {visibleTab === "equipment" && <Equipment onNavigateToManufacturers={(partContext) => {
        setPendingPartReturn(partContext);
        setMfrAutoAdd(true);
        setActiveTab("manufacturers");
      }} />}
      {visibleTab === "inspections" && <Inspections />}
      {visibleTab === "users" && <UsersPanel />}
      {visibleTab === "dispatchCategories" && (
        <div className="space-y-3">
          <FieldReadinessPanel />
          <DispatchEmailPanel />
          <DispatchCategoriesPanel />
          {can("settings.geocoding", "read") && <GeocodingPanel />}
        </div>
      )}
      {visibleTab === "integrations" && (
        <IntegrationsTab initialSection={integrationsSection} slaCount={slaCount} slaTitle={slaTitle} />
      )}
      {visibleTab === "auditLog" && <AuditLogPanel />}
      {visibleTab === "wiki" && (
        <SettingsPanel>
          <WikiSection expanded={true} toggle={() => {}} />
        </SettingsPanel>
      )}
      {visibleTab === "general" && (
    <div className="space-y-3">
      {/* iter266/267 — General uses the shared sub-tab strip; one section
          renders at a time inside the shared panel shell. */}
      <SubTabs
        items={GENERAL_SECTIONS}
        value={generalSection}
        onChange={openGeneralSection}
        testId="general-subtabs"
        testIdPrefix="general-subtab"
        searchable
        actions={
          <button type="button" className={`${btn("write")} h-8 w-8 justify-center !px-0`} onClick={() => saveSettings(settings)} disabled={saving}
            title={saving ? "Saving…" : "Save all changes"} aria-label={tr("Save All Changes")} data-testid="btn-save-settings">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          </button>
        }
      />

      <SettingsPanel>
        <div className="divide-y">
          {/* Default Rates Section */}
          {generalSection === "defaults" && (
          <div>
            <PanelHeader icon={GearIcon} title={tr("Default Rates")} hint="Applied to new proposals &amp; work orders only" testId="section-defaults" />
            {expandedSections.defaults && (
              <div className="px-5 pb-5 space-y-4">
                <Callout tone="warn">Changes here only affect NEW proposals and work orders. Existing ones are not modified.</Callout>
                <div className="grid grid-cols-5 gap-4">
                  <div>
                    <Label className="text-xs">Default Labor Cost ($/hr)</Label>
                    <Input type="number" step="0.5" value={settings.default_labor_cost ?? ""} onChange={(e) => setSettings(prev => ({ ...prev, default_labor_cost: parseFloat(e.target.value) || 0 }))} className="h-9" data-testid="input-default-labor-cost" />
                  </div>
                  <div>
                    <Label className="text-xs">Default Labor GM%</Label>
                    <Input type="number" step="1" value={Math.round(settings.default_labor_margin || 0)} onChange={(e) => setSettings(prev => ({ ...prev, default_labor_margin: parseFloat(e.target.value) || 0 }))} className="h-9" data-testid="input-default-labor-margin" />
                  </div>
                  <div>
                    <Label className="text-xs">Total Labor Rate ($/hr)</Label>
                    <div className="h-9 flex items-center px-3 bg-slate-50 rounded-md text-sm font-semibold tabular-nums" data-testid="display-total-labor-rate">
                      {(() => {
                        const c = parseFloat(settings.default_labor_cost) || 0;
                        const gm = parseFloat(settings.default_labor_margin) || 0;
                        const rate = gm >= 100 ? c : Math.ceil(c / (1 - gm / 100));
                        return `$${rate}/hr`;
                      })()}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Default Material GM%</Label>
                    <Input type="number" step="1" value={Math.round(settings.default_material_margin || 0)} onChange={(e) => setSettings(prev => ({ ...prev, default_material_margin: parseFloat(e.target.value) || 0 }))} className="h-9" data-testid="input-default-material-margin" />
                  </div>
                  <div>
                    {/* iter233 — default freight GM% applied to NEW proposal
                        and CO line items (sell = cost / (1 − GM%)). Editable
                        per line item in the workups. */}
                    <Label className="text-xs">Default Freight GM%</Label>
                    <Input type="number" step="1" max="99" value={Math.round(settings.default_freight_margin ?? 15)} onChange={(e) => setSettings(prev => ({ ...prev, default_freight_margin: parseFloat(e.target.value) || 0 }))} className="h-9" data-testid="input-default-freight-margin" />
                  </div>
                  {/* iter254 — Drive Time rate trio (Cost / GM% / Total),
                      mirrors the Labor row. Seeds every new proposal scope's
                      `drive_time_gm` so drive time is billed consistently. */}
                  <div>
                    <Label className="text-xs">Default Drive Time Cost ($/hr)</Label>
                    <Input
                      type="number" step="0.5"
                      value={settings.default_drive_time_cost ?? ""}
                      onChange={(e) => setSettings(prev => ({ ...prev, default_drive_time_cost: parseFloat(e.target.value) || 0 }))}
                      className="h-9"
                      data-testid="input-default-drive-time-cost"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Default Drive Time GM%</Label>
                    <Input
                      type="number" step="1"
                      value={Math.round(settings.default_drive_time_margin ?? 0)}
                      onChange={(e) => setSettings(prev => ({ ...prev, default_drive_time_margin: parseFloat(e.target.value) || 0 }))}
                      className="h-9"
                      data-testid="input-default-drive-time-margin"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Total Drive Time Rate ($/hr)</Label>
                    <div className="h-9 flex items-center px-3 bg-slate-50 rounded-md text-sm font-semibold tabular-nums" data-testid="display-total-drive-time-rate">
                      {(() => {
                        const c = parseFloat(settings.default_drive_time_cost) || 0;
                        const gm = parseFloat(settings.default_drive_time_margin) || 0;
                        const rate = gm >= 100 ? c : Math.ceil(c / (1 - gm / 100));
                        return `$${rate}/hr`;
                      })()}
                    </div>
                  </div>
                  <div>
                    {/* Default CC Fee % — applied silently to Material/Labor
                        on every NEW proposal, WO, CO, and Parts Counter
                        quote. Existing docs keep their own saved value.
                        Set 0 = no fee baked in. Customer-facing PDFs never
                        show this as a separate line — it's distributed
                        proportionally into Material/Labor pricing. */}
                    <Label className="text-xs">Default CC Fee %</Label>
                    <Input type="number" step="0.1" value={settings.default_cc_fee_pct ?? 0} onChange={(e) => setSettings(prev => ({ ...prev, default_cc_fee_pct: parseFloat(e.target.value) || 0 }))} className="h-9" data-testid="input-default-cc-fee" />
                  </div>
                </div>
                {/* iter168 Q1: admin-controlled toggle for whether the CC
                    fee base includes freight on Parts Counter orders.
                    Default true preserves legacy behavior (fee on freight). */}
                <div className="flex items-start justify-between gap-4 p-3 bg-slate-50 border border-slate-200 rounded-md">
                  <div className="flex-1">
                    <Label className="text-sm font-medium text-slate-700">Apply CC Fee to Freight (Parts Counter)</Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      ON: CC fee is calculated on subtotal + tax + freight (legacy — fee applies to the full charge).
                      <br />
                      OFF: CC fee is calculated on subtotal + tax only, then freight is added after (no fee on freight).
                    </p>
                  </div>
                  <label className="inline-flex items-center gap-2 mt-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.cc_fee_includes_freight !== false}
                      onChange={(e) => setSettings(prev => ({ ...prev, cc_fee_includes_freight: e.target.checked }))}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      data-testid="toggle-cc-fee-includes-freight"
                    />
                    <span className="text-xs font-medium text-slate-700">{settings.cc_fee_includes_freight !== false ? "ON" : "OFF"}</span>
                  </label>
                </div>
                {/* iter233 — DOT truck/trailer ID lists used by the DOT
                    Driver's Vehicle Inspection Report dropdowns. */}
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-md space-y-3" data-testid="dot-vehicles-section">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">{tr("DOT Vehicles")}</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Truck/Van and Trailer IDs shown on the DOT Driver's Vehicle Inspection Report. Comma-separated — add or remove as your fleet changes.</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{tr("Trucks / Vans")}</Label>
                      <Input
                        value={(settings.dot_trucks || []).join(", ")}
                        onChange={(e) => setSettings(prev => ({ ...prev, dot_trucks: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))}
                        placeholder="AERY58, DJK099, …"
                        className="h-9"
                        data-testid="input-dot-trucks"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{tr("Trailers (DOT inspection required)")}</Label>
                      <Input
                        value={(settings.dot_trailers || []).join(", ")}
                        onChange={(e) => setSettings(prev => ({ ...prev, dot_trailers: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))}
                        placeholder="BNOL83, BVXG69, …"
                        className="h-9"
                        data-testid="input-dot-trailers"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Freight Estimate Table Section */}
          {generalSection === "freight" && (
          <div>
            <PanelHeader icon={Truck} title="Freight Estimate Table" hint="Weight-based UPS rate estimates" testId="section-freight" />
            {expandedSections.freight && (
              <div className="px-5 pb-5 space-y-4">
                <p className="text-xs text-slate-500">Standard UPS ground rate estimates by weight. These rates auto-fill freight on Parts Counter orders when weight is entered.</p>
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold w-36">{tr("Label")}</TableHead>
                    <TableHead className="text-xs font-semibold w-28">Max Weight (lbs)</TableHead>
                    <TableHead className="text-xs font-semibold w-28">Rate ($)</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(settings.freight_estimate_table || []).map((row, idx) => (
                      // eslint-disable-next-line react/no-array-index-key -- editable freight rate rows; updates write back via ft[idx]
                      <TableRow key={`fr-${idx}`}>
                        <TableCell><Input value={row.label} onChange={e => {
                          const ft = [...(settings.freight_estimate_table || [])];
                          ft[idx] = { ...ft[idx], label: e.target.value };
                          setSettings(prev => ({ ...prev, freight_estimate_table: ft }));
                        }} className="h-7 text-xs" /></TableCell>
                        <TableCell><Input type="number" value={row.max_weight} onChange={e => {
                          const ft = [...(settings.freight_estimate_table || [])];
                          ft[idx] = { ...ft[idx], max_weight: parseFloat(e.target.value) || 0 };
                          setSettings(prev => ({ ...prev, freight_estimate_table: ft }));
                        }} className="h-7 text-xs" /></TableCell>
                        <TableCell><Input type="number" step="0.01" value={row.rate} onChange={e => {
                          const ft = [...(settings.freight_estimate_table || [])];
                          ft[idx] = { ...ft[idx], rate: parseFloat(e.target.value) || 0 };
                          setSettings(prev => ({ ...prev, freight_estimate_table: ft }));
                        }} className="h-7 text-xs" /></TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => {
                            const ft = (settings.freight_estimate_table || []).filter((_, i) => i !== idx);
                            setSettings(prev => ({ ...prev, freight_estimate_table: ft }));
                          }}><Trash2 className="w-3 h-3" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button variant="outline" size="sm" onClick={() => {
                  const ft = [...(settings.freight_estimate_table || []), { label: "", max_weight: 0, rate: 0 }];
                  setSettings(prev => ({ ...prev, freight_estimate_table: ft }));
                }} data-testid="add-freight-row"><Plus className="w-3.5 h-3.5 mr-1" />Add Weight Bracket</Button>
              </div>
            )}
          </div>
          )}

          {/* Tax Decision Tree Section */}
          {generalSection === "tax" && (
          <div>
            <PanelHeader icon={Receipt} title={tr("Tax Decision Tree")} hint="State, county &amp; city tax rates" testId="section-tax" />
            {expandedSections.tax && (
              <div className="px-5 pb-5 space-y-6">
                <Callout tone="warn">Tax rates auto-apply to new proposals &amp; work orders based on site address. Existing documents are not changed.</Callout>

                {/* PDF Reference & Viewer */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Label className="text-xs shrink-0">Decision Tree PDF URL:</Label>
                    <Input type="text" placeholder="Paste PDF URL" value={settings.tax_decision_tree_pdf_url || ""} onChange={(e) => setSettings(prev => ({ ...prev, tax_decision_tree_pdf_url: e.target.value }))} className="h-8 text-xs flex-1" data-testid="input-pdf-url" />
                  </div>
                  {settings.tax_decision_tree_pdf_url && (
                    <div className="border rounded-lg overflow-hidden bg-slate-50" data-testid="pdf-viewer">
                      <iframe
                        src={settings.tax_decision_tree_pdf_url}
                        className="w-full"
                        style={{ height: "500px" }}
                        title="Tax Decision Tree PDF"
                      />
                    </div>
                  )}
                </div>

                {/* Step 0 — Customer Overrides. Checked BEFORE the
                    city/county/state tree: a customer listed here always
                    bills at this flat rate + tax no, no matter where the
                    jobsite is and regardless of an exemption certificate. */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Step 0 — Customer Overrides</Label>
                      <p className="text-[11px] text-slate-400 mt-0.5">Checked first. Wins over the jobsite address tree <span className="font-medium">and</span> over Tax Exempt.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={newOverrideClient} onValueChange={setNewOverrideClient}>
                        <SelectTrigger className="h-7 w-56 text-xs" data-testid="select-tax-override-customer">
                          <SelectValue placeholder={tr("Pick a customer…")} />
                        </SelectTrigger>
                        <SelectContent>
                          {clientOptions.map(c => (
                            <SelectItem key={c.id} value={c.id} data-testid={`tax-override-client-opt-${c.id}`}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addCustomerOverride} data-testid="btn-add-tax-override"><Plus className="w-3 h-3 mr-1" />{tr("Add Override")}</Button>
                    </div>
                  </div>
                  {customerOverrides.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2" data-testid="tax-overrides-empty">No customer overrides — every customer follows the address tree below.</p>
                  ) : (
                    <Table>
                      <TableHeader><TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-semibold">{tr("Customer")}</TableHead>
                        <TableHead className="text-xs font-semibold w-28">{tr("Rate %")}</TableHead>
                        <TableHead className="text-xs font-semibold w-28">{tr("Tax No")}</TableHead>
                        <TableHead className="text-xs font-semibold">{tr("Label")}</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {customerOverrides.map(ov => (
                          <TableRow key={ov.client_id} data-testid={`tax-override-row-${ov.client_id}`}>
                            <TableCell className="text-sm font-medium">{ov.client_name || ov.client_id}</TableCell>
                            <TableCell><Input type="number" step="0.01" value={ov.rate} onChange={(e) => updateCustomerOverride(ov.client_id, "rate", parseFloat(e.target.value) || 0)} className="h-7 w-20 text-sm" data-testid={`tax-override-rate-${ov.client_id}`} /></TableCell>
                            <TableCell><Input value={ov.tax_no || ""} onChange={(e) => updateCustomerOverride(ov.client_id, "tax_no", e.target.value)} className="h-7 text-sm w-24" placeholder="e.g. CODR" data-testid={`tax-override-tax-no-${ov.client_id}`} /></TableCell>
                            <TableCell><Input value={ov.label || ""} onChange={(e) => updateCustomerOverride(ov.client_id, "label", e.target.value)} className="h-7 text-sm" placeholder="e.g. Denver only" data-testid={`tax-override-label-${ov.client_id}`} /></TableCell>
                            <TableCell><Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeCustomerOverride(ov.client_id)} data-testid={`btn-remove-tax-override-${ov.client_id}`}><Trash2 className="w-3 h-3" /></Button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>


                {/* State Rates */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr("State Rates")}</Label>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addState} data-testid="btn-add-state"><Plus className="w-3 h-3 mr-1" />{tr("Add State")}</Button>
                  </div>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-semibold w-24">{tr("State")}</TableHead>
                      <TableHead className="text-xs font-semibold w-28">{tr("Rate %")}</TableHead>
                      <TableHead className="text-xs font-semibold w-24">{tr("Tax No")}</TableHead>
                      <TableHead className="text-xs font-semibold">Exempt Allowed</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {Object.entries(taxRates.states || {}).map(([abbr, info]) => (
                        <TableRow key={abbr}>
                          <TableCell className="text-sm font-medium">{abbr}</TableCell>
                          <TableCell><Input type="number" step="0.01" value={info.rate} onChange={(e) => updateTaxField("states", abbr, "rate", parseFloat(e.target.value) || 0)} className="h-7 w-20 text-sm" /></TableCell>
                          <TableCell><Input value={info.tax_no || ""} onChange={(e) => updateTaxField("states", abbr, "tax_no", e.target.value)} className="h-7 text-sm w-24" placeholder="e.g. CO-ST" data-testid={`state-tax-no-${abbr}`} /></TableCell>
                          <TableCell>
                            <input type="checkbox" checked={info.exempt_allowed || false} onChange={(e) => updateTaxField("states", abbr, "exempt_allowed", e.target.checked)} className="h-4 w-4 rounded" />
                          </TableCell>
                          <TableCell><Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeEntry("states", abbr)}><Trash2 className="w-3 h-3" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* County/District Rates */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr("County / District Rates")}</Label>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addCounty} data-testid="btn-add-county"><Plus className="w-3 h-3 mr-1" />{tr("Add County")}</Button>
                  </div>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-semibold">County</TableHead>
                      <TableHead className="text-xs font-semibold w-20">{tr("State")}</TableHead>
                      <TableHead className="text-xs font-semibold w-28">{tr("Rate %")}</TableHead>
                      <TableHead className="text-xs font-semibold">{tr("Type")}</TableHead>
                      <TableHead className="text-xs font-semibold w-24">{tr("Tax No")}</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {Object.entries(taxRates.counties || {}).map(([name, info]) => (
                        <TableRow key={name}>
                          <TableCell className="text-sm font-medium">{name}</TableCell>
                          <TableCell className="text-sm text-slate-500">{info.state}</TableCell>
                          <TableCell><Input type="number" step="0.01" value={info.rate} onChange={(e) => updateTaxField("counties", name, "rate", parseFloat(e.target.value) || 0)} className="h-7 w-20 text-sm" /></TableCell>
                          <TableCell><Input value={info.type || ""} onChange={(e) => updateTaxField("counties", name, "type", e.target.value)} className="h-7 text-sm w-24" placeholder="e.g. RTD/CD" /></TableCell>
                          <TableCell><Input value={info.tax_no || ""} onChange={(e) => updateTaxField("counties", name, "tax_no", e.target.value)} className="h-7 text-sm w-24" placeholder="e.g. CORCD" data-testid={`county-tax-no-${name}`} /></TableCell>
                          <TableCell><Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeEntry("counties", name)}><Trash2 className="w-3 h-3" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* City Rates */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr("City Rates")}</Label>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addCity} data-testid="btn-add-city"><Plus className="w-3 h-3 mr-1" />{tr("Add City")}</Button>
                  </div>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-semibold">{tr("City")}</TableHead>
                      <TableHead className="text-xs font-semibold w-20">{tr("State")}</TableHead>
                      <TableHead className="text-xs font-semibold w-28">{tr("Rate %")}</TableHead>
                      <TableHead className="text-xs font-semibold w-24">{tr("Tax No")}</TableHead>
                      <TableHead className="text-xs font-semibold">Never Exempt</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {Object.entries(taxRates.cities || {}).map(([name, info]) => (
                        <TableRow key={name}>
                          <TableCell className="text-sm font-medium">{name}</TableCell>
                          <TableCell className="text-sm text-slate-500">{info.state}</TableCell>
                          <TableCell><Input type="number" step="0.01" value={info.rate} onChange={(e) => updateTaxField("cities", name, "rate", parseFloat(e.target.value) || 0)} className="h-7 w-20 text-sm" /></TableCell>
                          <TableCell><Input value={info.tax_no || ""} onChange={(e) => updateTaxField("cities", name, "tax_no", e.target.value)} className="h-7 text-sm w-24" placeholder="e.g. CODR" data-testid={`city-tax-no-${name}`} /></TableCell>
                          <TableCell>
                            <input type="checkbox" checked={info.never_exempt || false} onChange={(e) => updateTaxField("cities", name, "never_exempt", e.target.checked)} className="h-4 w-4 rounded" />
                          </TableCell>
                          <TableCell><Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeEntry("cities", name)}><Trash2 className="w-3 h-3" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Service Types Section */}
          {generalSection === "services" && (
          <div>
            <PanelHeader icon={ClipboardCheck} title={tr("Service Types")} hint={`${serviceTypes.length} types`} testId="section-services" />
            {expandedSections.services && (
              <div className="px-5 pb-5 space-y-3">
                <div className="flex gap-2">
                  <Input value={newServiceType} onChange={(e) => setNewServiceType(e.target.value)} placeholder="New service type name..." className="h-8 text-sm" data-testid="input-new-service-type"
                    onKeyDown={(e) => { if (e.key === "Enter") handleAddServiceType(); }} />
                  <Button size="sm" onClick={handleAddServiceType} data-testid="btn-add-service-type"><Plus className="w-3.5 h-3.5 mr-1" />{tr("Add")}</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {serviceTypes.map(st => (
                    <div key={st.id} className="flex items-center gap-1.5 bg-slate-100 rounded-full px-3 py-1 text-sm" data-testid={`service-type-${st.id}`}>
                      <span>{st.name}</span>
                      <button className="text-slate-400 hover:text-red-500" onClick={() => handleDeleteServiceType(st.id)}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Technicians Section */}

          {/* Account Managers Section */}

          {/* Project Managers Section */}

          {/* AR Reminder Settings Section */}
          {generalSection === "arReminders" && (
            <ARReminderSettingsSection expandedSections={expandedSections} toggle={toggle} />
          )}

          {/* iter265 — Foundation Approval Queue, Bridge Ops, Miter,
              Data Management and Go-Live Data Cleanup moved to the
              Integrations tab (admin-allowlisted). */}

          {/* Timekeep Settings — shop geofence + timekeeping defaults all in
              one place. Lives under Data Management per admin preference.
              Changes persist immediately; GPS classifier and variance review
              pick up the new values on the next request (no restart). */}
          {generalSection === "shop" && (
          <div>
            <PanelHeader icon={MapPin} title={tr("Timekeeping")} hint="Shop geofence · clock-in rules · variance thresholds" testId="section-shop" />
            {expandedSections.shop && (
              <div className="px-5 pb-5 space-y-5">
                <Callout>Changes persist immediately. Geocode the shop address whenever it changes, then save. GPS classifier and variance review pick up the new values on the next request.</Callout>
                <div>
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{tr("Shop / Company HQ")}</div>
                  <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-6">
                    <Label className="text-xs">{tr("Street Address")}</Label>
                    <Input
                      value={settings.shop_location?.address ?? ""}
                      onChange={e => setSettings(prev => ({ ...prev, shop_location: { ...(prev.shop_location || {}), address: e.target.value } }))}
                      className="h-9" placeholder="1785 W. 160th Ave."
                      data-testid="input-shop-address" />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">{tr("City")}</Label>
                    <Input
                      value={settings.shop_location?.city ?? ""}
                      onChange={e => setSettings(prev => ({ ...prev, shop_location: { ...(prev.shop_location || {}), city: e.target.value } }))}
                      className="h-9" data-testid="input-shop-city" />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-xs">{tr("State")}</Label>
                    <Input
                      value={settings.shop_location?.state ?? ""}
                      onChange={e => setSettings(prev => ({ ...prev, shop_location: { ...(prev.shop_location || {}), state: e.target.value.toUpperCase() } }))}
                      maxLength={2}
                      className="h-9 uppercase" data-testid="input-shop-state" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">{tr("Zip")}</Label>
                    <Input
                      value={settings.shop_location?.zip ?? ""}
                      onChange={e => setSettings(prev => ({ ...prev, shop_location: { ...(prev.shop_location || {}), zip: e.target.value } }))}
                      className="h-9" data-testid="input-shop-zip" />
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-3 items-end mt-2">
                  <div className="col-span-3">
                    <Label className="text-xs">{tr("Latitude")}</Label>
                    <Input type="number" step="0.000001"
                      value={settings.shop_location?.lat ?? ""}
                      onChange={e => setSettings(prev => ({ ...prev, shop_location: { ...(prev.shop_location || {}), lat: parseFloat(e.target.value) || null } }))}
                      className="h-9 tabular-nums" data-testid="input-shop-lat" />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">{tr("Longitude")}</Label>
                    <Input type="number" step="0.000001"
                      value={settings.shop_location?.lng ?? ""}
                      onChange={e => setSettings(prev => ({ ...prev, shop_location: { ...(prev.shop_location || {}), lng: parseFloat(e.target.value) || null } }))}
                      className="h-9 tabular-nums" data-testid="input-shop-lng" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">{tr("Geofence (ft)")}</Label>
                    <Input type="number" step="50" min="100"
                      value={settings.shop_location?.radius_ft ?? 500}
                      onChange={e => setSettings(prev => ({ ...prev, shop_location: { ...(prev.shop_location || {}), radius_ft: parseInt(e.target.value) || 500 } }))}
                      className="h-9 tabular-nums" data-testid="input-shop-radius" />
                  </div>
                  <div className="col-span-4">
                    <Button variant="outline" className="h-9 w-full gap-1" data-testid="btn-shop-geocode"
                      onClick={async () => {
                        const s = settings.shop_location || {};
                        const full = `${s.address || ""}, ${s.city || ""}, ${s.state || ""} ${s.zip || ""}`.trim();
                        if (full.replace(/[ ,]/g, "").length === 0) { toast.error("Enter an address first"); return; }
                        try {
                          const res = await geocodeAddress({ address: full });
                          setSettings(prev => ({ ...prev, shop_location: { ...(prev.shop_location || {}), lat: res.data.lat, lng: res.data.lng } }));
                          toast.success(`Geocoded · ${res.data.display_name?.slice(0, 48) || "ok"}`);
                        } catch { toast.error("Address not found"); }
                      }}>
                      <MapPin className="w-3.5 h-3.5" />{tr("Geocode Address")}
                    </Button>
                  </div>
                </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{tr("Clock-In / Clock-Out Rules")}</div>
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-3">
                      <Label className="text-xs">{tr("Default Lunch (min)")}</Label>
                      <Input type="number" step="15" min="0"
                        value={settings.timekeep?.default_lunch_minutes ?? 30}
                        onChange={e => setSettings(prev => ({ ...prev, timekeep: { ...(prev.timekeep || {}), default_lunch_minutes: parseInt(e.target.value) || 0 } }))}
                        className="h-9 tabular-nums" data-testid="input-tk-lunch-minutes" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">{tr("Default Hours / Day")}</Label>
                      <Input type="number" step="0.25" min="0"
                        value={settings.timekeep?.default_hours ?? 10}
                        onChange={e => setSettings(prev => ({ ...prev, timekeep: { ...(prev.timekeep || {}), default_hours: parseFloat(e.target.value) || 0 } }))}
                        className="h-9 tabular-nums" data-testid="input-tk-default-hours" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">{tr("Variance Threshold (%)")}</Label>
                      <Input type="number" step="1" min="0" max="50"
                        value={settings.timekeep?.variance_threshold_pct ?? 5}
                        onChange={e => setSettings(prev => ({ ...prev, timekeep: { ...(prev.timekeep || {}), variance_threshold_pct: parseFloat(e.target.value) || 0 } }))}
                        className="h-9 tabular-nums" data-testid="input-tk-variance-pct" />
                    </div>
                    <div className="col-span-3 flex items-end">
                      <label className="flex items-center gap-2 text-sm text-slate-700 pb-1.5 cursor-pointer">
                        <input type="checkbox"
                          checked={settings.timekeep?.lunch_default_yes ?? true}
                          onChange={e => setSettings(prev => ({ ...prev, timekeep: { ...(prev.timekeep || {}), lunch_default_yes: e.target.checked } }))}
                          className="w-4 h-4 rounded border-slate-300"
                          data-testid="input-tk-lunch-default-yes" />
                        Lunch defaults to <b className="font-semibold">{tr("Yes")}</b>
                      </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-3 mt-2">
                    <div className="col-span-3">
                      <Label className="text-xs">{tr("Arrive Dwell (sec)")}</Label>
                      <Input type="number" step="30" min="0"
                        value={settings.timekeep?.geofence_dwell_arrive_s ?? 120}
                        onChange={e => setSettings(prev => ({ ...prev, timekeep: { ...(prev.timekeep || {}), geofence_dwell_arrive_s: parseInt(e.target.value) || 0 } }))}
                        className="h-9 tabular-nums" data-testid="input-tk-dwell-arrive" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">{tr("Leave Dwell (sec)")}</Label>
                      <Input type="number" step="30" min="0"
                        value={settings.timekeep?.geofence_dwell_leave_s ?? 300}
                        onChange={e => setSettings(prev => ({ ...prev, timekeep: { ...(prev.timekeep || {}), geofence_dwell_leave_s: parseInt(e.target.value) || 0 } }))}
                        className="h-9 tabular-nums" data-testid="input-tk-dwell-leave" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">{tr("Review Day")}</Label>
                      <Select value={String(settings.timekeep?.variance_review_dow ?? 0)}
                        onValueChange={v => setSettings(prev => ({ ...prev, timekeep: { ...(prev.timekeep || {}), variance_review_dow: parseInt(v) } }))}>
                        <SelectTrigger className="h-9" data-testid="select-tk-review-dow"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => (
                            <SelectItem key={d} value={String(i)}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Review Hour (local)</Label>
                      <Input type="number" step="1" min="0" max="23"
                        value={settings.timekeep?.variance_review_hour ?? 6}
                        onChange={e => setSettings(prev => ({ ...prev, timekeep: { ...(prev.timekeep || {}), variance_review_hour: parseInt(e.target.value) || 0 } }))}
                        className="h-9 tabular-nums" data-testid="input-tk-review-hour" />
                    </div>
                  </div>
                  {/* #52 — Lunch-vs-GPS validator threshold. Variance
                      reviewer flags a timesheet when the tech was
                      on-site (per dispatch_events) for more than this
                      many minutes of their declared lunch window. */}
                  <div className="grid grid-cols-12 gap-3 mt-2">
                    <div className="col-span-4">
                      <Label className="text-xs">Lunch-on-site Threshold (min)</Label>
                      <Input type="number" step="1" min="0" max="120"
                        value={settings.timekeep?.lunch_onsite_threshold_min ?? 15}
                        onChange={e => {
                          const raw = parseInt(e.target.value);
                          const v = Number.isFinite(raw) ? Math.max(0, Math.min(120, raw)) : 15;
                          setSettings(prev => ({ ...prev, timekeep: { ...(prev.timekeep || {}), lunch_onsite_threshold_min: v } }));
                        }}
                        className="h-9 tabular-nums" data-testid="input-tk-lunch-onsite-threshold" />
                      <p className="text-[10px] text-slate-500 mt-1">
                        Flags timesheets when a tech was on-site for more than this many minutes during their declared lunch.
                      </p>
                    </div>
                  </div>
                </div>

                <Button onClick={() => saveSettings({ shop_location: settings.shop_location || {}, timekeep: settings.timekeep || {} })} disabled={saving} className="h-9" data-testid="btn-save-shop">
                  {tr("Save Timekeep Settings")}
                </Button>
              </div>
            )}
          </div>
          )}

          {/* Push Notifications — moved out of Integrations (iter297).
              Same panel, same endpoints; location only. */}
          {generalSection === "payments" && (
          <div>
            <PanelHeader icon={CreditCard} title={tr("Payments")} hint="Customer-facing Square 'Pay Now' links on invoices and Parts Counter quotes" testId="section-payments" />
            {expandedSections.payments !== false && (
              <div className="px-5 pb-5 space-y-4">
                <Callout tone={settings.online_payments_enabled ? "info" : "warn"}>
                  {settings.online_payments_enabled
                    ? "Online payments are ON — new invoices and parts quotes get a Square payment link, PDFs carry the PAY NOW button and emails mention it."
                    : "Online payments are OFF — no Square links are created, every invoice / parts-quote PDF (including ones already saved) is rendered without the PAY NOW button, and customer emails only say \"view\"."}
                </Callout>
                <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="online-payments-row">
                  <input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={!!settings.online_payments_enabled}
                    onChange={(e) => { const on = e.target.checked; setSettings(prev => ({ ...prev, online_payments_enabled: on })); invalidateOnlinePayments(on); }}
                    data-testid="toggle-online-payments" />
                  <span className="text-sm text-slate-800">Enable online payments (Square Pay Now) on invoices and Parts Counter quotes</span>
                </label>
                <p className="text-xs text-slate-500">Turn this on once the Square account is live. Save with "Save All Changes".</p>
              </div>
            )}
          </div>
          )}

          {generalSection === "push" && (
            <div data-testid="section-push-wrapper">
              <PushNotificationsPanel />
            </div>
          )}

        </div>
      </SettingsPanel>
    </div>
      )}
    </div>
  );
}


function AccountManagers() {
  const [ams, setAMs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", zip_code: "", notes: "" });

  useEffect(() => { loadAMs(); }, []);

  const loadAMs = async () => {
    try { const res = await getAccountManagers(); setAMs(res.data); } catch {}
  };

  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", address: "", city: "", state: "", zip_code: "", notes: "" });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) {
        await updateAccountManager(editing, form);
        toast.success("Account manager updated");
      } else {
        await createAccountManager(form);
        toast.success("Account manager added");
      }
      resetForm();
      loadAMs();
    } catch { toast.error("Failed to save"); }
  };

  const handleEdit = (am) => {
    setEditing(am.id);
    setForm({ name: am.name || "", email: am.email || "", phone: am.phone || "", address: am.address || "", city: am.city || "", state: am.state || "", zip_code: am.zip_code || "", notes: am.notes || "" });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try { await deleteAccountManager(id); toast.success("Deleted"); loadAMs(); } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-4">
      {/* Existing AMs list */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">Account Managers ({ams.length})</h3>
        {!showForm && (
          <Button size="sm" variant="outline" onClick={() => { resetForm(); setShowForm(true); }} data-testid="am-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> {tr("Add")}
          </Button>
        )}
      </div>

      {ams.length === 0 && !showForm ? (
        <p className="text-sm text-slate-400 py-4 text-center">No account managers added yet</p>
      ) : (
        <div className="space-y-2">
          {ams.map(am => (
            <div key={am.id} className={`border rounded-lg p-3 flex items-start justify-between hover:bg-slate-50 ${editing === am.id ? "border-blue-300 bg-blue-50/30" : ""}`} data-testid={`am-row-${am.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-slate-800">{am.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {am.email && <span>{am.email}</span>}
                  {am.phone && <span className="ml-2">{am.phone}</span>}
                </div>
                {(am.address || am.city || am.state) && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {[am.address, am.city, am.state, am.zip_code].filter(Boolean).join(", ")}
                  </div>
                )}
                {am.notes && <div className="text-xs text-slate-500 mt-1 italic">"{am.notes}"</div>}
              </div>
              <div className="flex gap-1 flex-shrink-0 ml-2">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(am)} data-testid={`am-edit-${am.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(am.id)} data-testid={`am-delete-${am.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form — only visible when toggled */}
      {showForm && (
        <div className="border rounded-lg p-4 bg-slate-50/50 space-y-3">
          <h3 className="text-sm font-semibold text-slate-800">{editing ? "Edit Account Manager" : "New Account Manager"}</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={tr("Full name")} data-testid="am-name" />
            </div>
            <div>
              <Label className="text-xs">{tr("Email")}</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={tr("Email")} data-testid="am-email" />
            </div>
            <div>
              <Label className="text-xs">{tr("Phone")}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={tr("Phone")} data-testid="am-phone" />
            </div>
            <div>
              <Label className="text-xs">{tr("Address")}</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={tr("Street address")} data-testid="am-address" />
            </div>
            <div>
              <Label className="text-xs">{tr("City")}</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={tr("City")} data-testid="am-city" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">{tr("State")}</Label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} placeholder="ST" data-testid="am-state" />
              </div>
              <div>
                <Label className="text-xs">{tr("ZIP")}</Label>
                <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder={tr("ZIP")} data-testid="am-zip" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">{tr("Notes")}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes about this AM..." className="text-sm resize-y" rows={2} data-testid="am-notes" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} data-testid="am-save">{editing ? "Update" : "Add"}</Button>
            <Button variant="outline" size="sm" onClick={resetForm}>{tr("Cancel")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}


function ARReminderSettingsSection({ expandedSections, toggle }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testSendingTier, setTestSendingTier] = useState(null);
  const [sendingDiag, setSendingDiag] = useState(false);
  const [stats, setStats] = useState(null);
  const [togglingAutomation, setTogglingAutomation] = useState(false);

  useEffect(() => { loadSettings(); loadStats(); }, []);

  const loadSettings = async () => {
    try {
      const res = await getARReminderSettings();
      setSettings(res.data);
    } catch { /* defaults will be returned */ }
    finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const res = await getEmailStats();
      setStats(res.data);
    } catch { /* non-fatal, widget just won't render */ }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateARReminderSettings(settings);
      toast.success("Email settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleToggleAutomation = async () => {
    if (!stats || togglingAutomation) return;
    const next = !stats.automation_enabled;
    setTogglingAutomation(true);
    // Optimistic UI — flip the label immediately so the user gets
    // instant feedback; server response replaces it regardless.
    setStats(prev => prev ? { ...prev, automation_enabled: next } : prev);
    try {
      await setEmailAutomation(next);
      await loadStats();
      toast.success(next
        ? "Email automation ON — customers will receive automated emails"
        : "Email automation OFF — no customer emails will be sent");
    } catch {
      // Roll back optimistic update on error
      setStats(prev => prev ? { ...prev, automation_enabled: !next } : prev);
      toast.error("Failed to toggle automation");
    } finally {
      setTogglingAutomation(false);
    }
  };

  const handleTestSend = async (tier) => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Enter a valid email address above first");
      return;
    }
    setTestSendingTier(tier.tier);
    try {
      const res = await testSendARReminder({
        tier: tier.tier,
        tier_label: tier.label,
        // Send the user's currently-edited (possibly unsaved) wording
        subject_template: tier.subject_template,
        body_template: tier.body_template,
        to_email: testEmail,
      });
      const status = res.data?.status;
      if (status === "sent") {
        toast.success(`Test sent to ${testEmail}`);
      } else if (status === "skipped") {
        // #273 hardening — same silent-no-op class as Foundation push.
        // The test email was rendered but NOT delivered — must be
        // unmissable so admins don't think their config is working.
        toast.error("Test NOT sent — email automation is DISABLED", {
          description: "EMAIL_AUTOMATION_ENABLED is off in backend/.env. The email was rendered but not delivered. Toggle it ON at the top of AR Reminders to send.",
          duration: 10000,
        });
      } else if (status === "error") {
        toast.error(`Send failed: ${res.data?.error || "unknown"}`);
      } else {
        toast.success("Test request accepted");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test send failed");
    } finally {
      setTestSendingTier(null);
    }
  };

  const handleDiagnosticSend = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Enter a valid email address first");
      return;
    }
    setSendingDiag(true);
    try {
      const { data } = await sendTestEmail(testEmail);
      if (data.status === "sent") {
        toast.success(`Accepted by Resend for ${data.to}`, {
          description: `Message id ${data.resend_id || "—"}. If it never arrives, the block is on the receiving mail server (spam / spoof quarantine), not in ServiceOps.`,
          duration: 12000,
        });
      } else if (data.status === "skipped") {
        toast.error("Nothing was sent", {
          description: data.reason === "no_api_key"
            ? "RESEND_API_KEY is not set on this server."
            : data.reason === "reserved_test_tld"
              ? "That address uses a reserved test domain."
              : "Email automation is OFF — use the Automation button above.",
          duration: 12000,
        });
      } else {
        toast.error("Resend rejected the message", { description: data.error || "unknown error", duration: 12000 });
      }
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test send failed");
    } finally {
      setSendingDiag(false);
    }
  };

  const updateTier = (idx, field, value) => {
    setSettings(prev => {
      const tiers = [...prev.tiers];
      tiers[idx] = { ...tiers[idx], [field]: value };
      return { ...prev, tiers };
    });
  };

  const expanded = expandedSections.arReminders;

  return (
    <div data-testid="ar-reminder-settings">
      <PanelHeader icon={Mail} title={tr("Email")} hint="Automated confirmations &amp; AR reminders" testId="section-arReminders" />
      {expanded && settings && (
        <div className="px-5 pb-5 space-y-4">
          <EmailAutomationSwitches />
          <DotDigestEmailSettings />
          <ConnectOutlookButton variant="panel" testId="settings-connect-outlook" />

          {/* ── Status & Quota ────────────────────────────────────── */}
          <div className="border rounded-lg p-3 bg-emerald-50/60 border-emerald-200 space-y-3" data-testid="email-status-panel">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span className="text-sm font-semibold text-emerald-900">Connected via Resend</span>
                </div>
                <div className="text-[11px] text-emerald-800/80 mt-0.5">
                  Reserved test TLDs blocked:
                  {" "}
                  <code className="text-[10px] bg-white px-1 py-0.5 rounded">.test</code>{" "}
                  <code className="text-[10px] bg-white px-1 py-0.5 rounded">.example</code>{" "}
                  <code className="text-[10px] bg-white px-1 py-0.5 rounded">.invalid</code>{" "}
                  <code className="text-[10px] bg-white px-1 py-0.5 rounded">.localhost</code>
                </div>
              </div>
              {stats && (
                <Button
                  type="button"
                  size="sm"
                  className={`h-8 text-[11px] shrink-0 ${
                    stats.automation_enabled
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-slate-200 hover:bg-slate-300 text-slate-700"
                  }`}
                  disabled={togglingAutomation}
                  onClick={handleToggleAutomation}
                  data-testid="btn-toggle-automation"
                >
                  {togglingAutomation ? "…" :
                    stats.automation_enabled ? "Automation: ON" : "Automation: OFF"}
                </Button>
              )}
            </div>

            {stats && (
              <div className="grid grid-cols-2 gap-3">
                {/* Today */}
                <div className="bg-white rounded border border-emerald-100 p-2">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Sent Today</span>
                    <span className={`text-[10px] font-semibold ${
                      stats.daily_pct >= 90 ? "text-rose-600" :
                      stats.daily_pct >= 75 ? "text-amber-600" :
                      "text-slate-500"
                    }`}>{stats.daily_pct}%</span>
                  </div>
                  <div className="text-lg font-bold text-slate-800 tabular-nums">
                    {stats.sent_today} <span className="text-xs text-slate-400 font-normal">/ {stats.daily_limit}</span>
                  </div>
                  <div className="h-1 bg-slate-100 rounded overflow-hidden mt-1">
                    <div
                      className={`h-full ${
                        stats.daily_pct >= 90 ? "bg-rose-500" :
                        stats.daily_pct >= 75 ? "bg-amber-500" :
                        "bg-emerald-500"
                      }`}
                      style={{ width: `${Math.min(stats.daily_pct, 100)}%` }}
                    />
                  </div>
                </div>
                {/* Month */}
                <div className="bg-white rounded border border-emerald-100 p-2">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Sent This Month</span>
                    <span className={`text-[10px] font-semibold ${
                      stats.monthly_pct >= 90 ? "text-rose-600" :
                      stats.monthly_pct >= 75 ? "text-amber-600" :
                      "text-slate-500"
                    }`}>{stats.monthly_pct}%</span>
                  </div>
                  <div className="text-lg font-bold text-slate-800 tabular-nums">
                    {stats.sent_month} <span className="text-xs text-slate-400 font-normal">/ {stats.monthly_limit}</span>
                  </div>
                  <div className="h-1 bg-slate-100 rounded overflow-hidden mt-1">
                    <div
                      className={`h-full ${
                        stats.monthly_pct >= 90 ? "bg-rose-500" :
                        stats.monthly_pct >= 75 ? "bg-amber-500" :
                        "bg-emerald-500"
                      }`}
                      style={{ width: `${Math.min(stats.monthly_pct, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── 30-Day Health ─────────────────────────────────────── */}
          {stats && (
            <div className="border rounded-lg p-3 bg-slate-50 border-slate-200" data-testid="email-health-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-700">30-Day Health</span>
                <span className="text-[10px] text-slate-400">Bounce rate: {stats.bounce_rate_pct}%</span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-white rounded p-2 text-center border border-slate-100">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">{tr("Sent")}</div>
                  <div className="text-base font-bold text-emerald-600 tabular-nums">{stats.total_sent_30d}</div>
                </div>
                <div className="bg-white rounded p-2 text-center border border-slate-100">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">Bounced</div>
                  <div className={`text-base font-bold tabular-nums ${stats.bounces_30d > 0 ? "text-rose-600" : "text-slate-400"}`}>{stats.bounces_30d}</div>
                </div>
                <div className="bg-white rounded p-2 text-center border border-slate-100">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">{tr("Errors")}</div>
                  <div className={`text-base font-bold tabular-nums ${stats.errors_30d > 0 ? "text-amber-600" : "text-slate-400"}`}>{stats.errors_30d}</div>
                </div>
                <div className="bg-white rounded p-2 text-center border border-slate-100">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">Complaints</div>
                  <div className={`text-base font-bold tabular-nums ${stats.complaints_30d > 0 ? "text-rose-600" : "text-slate-400"}`}>{stats.complaints_30d}</div>
                </div>
              </div>
            </div>
          )}

          {/* Test Send — preview the rendered wording in your inbox */}
          <div className="border rounded-lg p-3 bg-blue-50/40 border-blue-200 space-y-2">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-slate-700">{tr("Test Send")}</span>
              <span className="text-[10px] text-slate-500">— preview each tier's rendered email in your own inbox before going live</span>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-[10px] text-slate-500 shrink-0">{tr("Send Tests to")}</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@absinc.com"
                className="h-7 text-xs flex-1"
                data-testid="ar-test-email"
              />
              <span className="text-[10px] text-slate-400 shrink-0">→ click "Test" on any tier below</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-[10px] border-blue-300 text-blue-700 hover:bg-blue-50"
                disabled={sendingDiag}
                onClick={handleDiagnosticSend}
                data-testid="btn-email-diagnostic-send"
              >
                {sendingDiag ? "Sending…" : "Send plain test email"}
              </Button>
              <span className="text-[10px] text-slate-500">
                Uses the exact same path as tech sign-in links and schedule emails, and reports the true result.
              </span>
            </div>
          </div>

          {/* iter309 — "Sent" only means Resend accepted the message.
              Without the Resend webhook we never learn about bounces or
              spam quarantines, so say so instead of implying delivery. */}
          {/* iter309 — the production symptom: no RESEND_API_KEY on the
              server means every email is silently skipped. Say it loudly. */}
          {stats && stats.resend_key_present === false && (
            <div className="border rounded-lg p-3 bg-rose-50 border-rose-300" data-testid="email-no-key-warning">
              <div className="text-xs font-semibold text-rose-900">No email can be sent from this server</div>
              <div className="text-[11px] text-rose-800/90 mt-1 leading-snug">
                <code className="bg-white px-1 rounded">RESEND_API_KEY</code> is not configured here, so every
                email — tech sign-in links, schedule emails, invoice reminders — is skipped silently.
                Redeploy with the backend environment file included, then reload this panel.
              </div>
            </div>
          )}

          {stats && !stats.delivery_confirmations_seen && (
            <div className="border rounded-lg p-3 bg-amber-50 border-amber-200" data-testid="email-delivery-warning">
              <div className="text-xs font-semibold text-amber-900">{tr("Delivery is unconfirmed")}</div>
              <div className="text-[11px] text-amber-800/90 mt-1 leading-snug">
                No delivery or bounce confirmation has ever come back from Resend, so "Sent" here only
                means Resend <strong>accepted</strong> the message — it does not prove it reached the inbox.
                Add a webhook in the Resend dashboard pointing at <code className="bg-white px-1 rounded">/api/webhooks/resend</code>
                {" "}(events: delivered, bounced, delivery_delayed, complained) and this panel will start
                showing real delivery, plus bounces will surface as failed emails.
                {stats.from_email ? (
                  <> Mail is sent as <strong>{stats.from_email}</strong> — if that is your own domain,
                  check your Microsoft 365 anti-spoof quarantine for the missing messages.</>
                ) : null}
              </div>
            </div>
          )}

          {/* Lien watch digest (AR) */}
          <LienDigestSettings />

          {/* Escalation Tiers */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-slate-700">AR Escalation Tiers</h4>
            {(settings.tiers || []).map((tier, idx) => (
              <div key={tier.tier} className="border rounded-lg p-3 bg-slate-50/50 space-y-2" data-testid={`ar-tier-${tier.tier}`}>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={tier.enabled} onChange={(e) => updateTier(idx, "enabled", e.target.checked)} className="h-3.5 w-3.5 accent-blue-600 rounded" data-testid={`ar-tier-${tier.tier}-enabled`} />
                    <span className="font-semibold">{tier.label}</span>
                  </label>
                  <div className="flex items-center gap-1 ml-auto">
                    <span className="text-[10px] text-slate-500">{tr("Send at")}</span>
                    <Input type="number" value={tier.days} onChange={(e) => updateTier(idx, "days", parseInt(e.target.value) || 0)} className="h-7 w-16 text-xs text-center" data-testid={`ar-tier-${tier.tier}-days`} />
                    <span className="text-[10px] text-slate-500">days</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-[10px] ml-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                      disabled={!tier.enabled || testSendingTier === tier.tier}
                      onClick={() => handleTestSend(tier)}
                      data-testid={`ar-tier-${tier.tier}-test-send`}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      {testSendingTier === tier.tier ? "Sending..." : "Test"}
                    </Button>
                  </div>
                </div>
                {tier.enabled && (
                  <>
                    <div>
                      <Label className="text-[10px] text-slate-500">{tr("Subject Template")}</Label>
                      <Input value={tier.subject_template} onChange={(e) => updateTier(idx, "subject_template", e.target.value)} className="h-7 text-xs" placeholder="Subject..." data-testid={`ar-tier-${tier.tier}-subject`} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500">{tr("Body Template")}</Label>
                      <Textarea value={tier.body_template} onChange={(e) => updateTier(idx, "body_template", e.target.value)} rows={3} className="text-xs" placeholder="Email body..." data-testid={`ar-tier-${tier.tier}-body`} />
                      <div className="text-[10px] text-slate-400 mt-1">
                        Variables: {"{invoice_number}"} {"{customer_name}"} {"{customer_contact}"} {"{total}"} {"{age_days}"} {"{wo_number}"} {"{project_address}"} {"{company_name}"} {"{company_phone}"}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* PM Alert */}
          <div className="border rounded-lg p-3 bg-slate-50/50 space-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={settings.pm_alert_enabled} onChange={(e) => setSettings(prev => ({ ...prev, pm_alert_enabled: e.target.checked }))} className="h-3.5 w-3.5 accent-blue-600 rounded" data-testid="ar-pm-alert-enabled" />
              <span className="font-semibold text-sm">PM Escalation Alert</span>
            </label>
            <div className="flex items-center gap-2 text-xs text-slate-600 ml-6">
              <span>Surface in Dashboard Priority Actions when invoice reaches</span>
              <Input type="number" value={settings.pm_alert_days} onChange={(e) => setSettings(prev => ({ ...prev, pm_alert_days: parseInt(e.target.value) || 35 }))} className="h-7 w-16 text-xs text-center" data-testid="ar-pm-alert-days" />
              <span>days</span>
            </div>
          </div>

          {/* Account Hold Reminder */}
          <div className="border rounded-lg p-3 bg-slate-50/50 space-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={settings.hold_reminder_enabled ?? true} onChange={(e) => setSettings(prev => ({ ...prev, hold_reminder_enabled: e.target.checked }))} className="h-3.5 w-3.5 accent-blue-600 rounded" data-testid="ar-hold-reminder-enabled" />
              <span className="font-semibold text-sm">Account Hold Reminder</span>
            </label>
            <div className="text-[10px] text-slate-500 ml-6">
              Recurring reminder sent to customers whose account is on hold (60+ days overdue)
            </div>
            {(settings.hold_reminder_enabled ?? true) && (
              <div className="flex items-center gap-2 text-xs text-slate-600 ml-6">
                <span>{tr("Send reminder")}</span>
                <select
                  value={settings.hold_reminder_frequency || "daily"}
                  onChange={(e) => setSettings(prev => ({ ...prev, hold_reminder_frequency: e.target.value }))}
                  className="h-7 text-xs border rounded px-2 bg-white"
                  data-testid="ar-hold-reminder-frequency"
                >
                  <option value="daily">{tr("Daily")}</option>
                  <option value="every_other_day">{tr("Every Other Day")}</option>
                  <option value="weekly">{tr("Weekly")}</option>
                </select>
              </div>
            )}
          </div>

          {/* Sales — Proposal Follow-Ups (own section, own save) */}
          <SalesFollowupSettings />

          {/* From Address (for when email is connected) */}
          <div className="border rounded-lg p-3 bg-slate-50/50 space-y-2">
            <h4 className="text-sm font-semibold text-slate-700">Email Sender (for when connected)</h4>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] text-slate-500">{tr("From Name")}</Label>
                <Input value={settings.from_name} onChange={(e) => setSettings(prev => ({ ...prev, from_name: e.target.value }))} placeholder="ABS Inc." className="h-7 text-xs" data-testid="ar-from-name" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">{tr("From Email")}</Label>
                <Input value={settings.from_email} onChange={(e) => setSettings(prev => ({ ...prev, from_email: e.target.value }))} placeholder="ar@absinc.com" className="h-7 text-xs" data-testid="ar-from-email" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">{tr("Reply-To")}</Label>
                <Input value={settings.reply_to} onChange={(e) => setSettings(prev => ({ ...prev, reply_to: e.target.value }))} placeholder="service@absinc.com" className="h-7 text-xs" data-testid="ar-reply-to" />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="btn-save-ar-settings" className="bg-green-600 hover:bg-green-700 text-white">
              {saving ? "Saving..." : "Save Reminder Settings"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Profile card
// Stand-in for real auth: the user enters their email once; the app
// matches it against the Account Managers list to auto-fill their name
// on Proposals, Work Orders, etc. When real auth (Graph) comes online,
// this falls back to window.ABS_AUTH.email transparently.
// ─────────────────────────────────────────────────────────────────────
function ProfileCard({ accountManagers }) {
  const [email, setEmail] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const load = () => {
      // Lazy-require to avoid circular imports
      const ce = localStorage.getItem("abs_user_email") || "";
      setEmail(ce);
    };
    load();
    window.addEventListener("abs-user-changed", load);
    return () => window.removeEventListener("abs-user-changed", load);
  }, []);

  const match = accountManagers.find(am => (am.email || "").toLowerCase() === email.toLowerCase());

  const save = (val) => {
    const v = (val || "").trim().toLowerCase();
    if (v) {
      localStorage.setItem("abs_user_email", v);
      const m = accountManagers.find(am => (am.email || "").toLowerCase() === v);
      if (m) {
        localStorage.setItem("abs_current_user", m.name);
        localStorage.setItem("abs_current_user_email", m.email);
        localStorage.setItem("abs_prepared_by", m.name);
        localStorage.setItem("abs_prepared_by_email", m.email);
      }
    } else {
      localStorage.removeItem("abs_user_email");
      localStorage.removeItem("abs_current_user");
      localStorage.removeItem("abs_current_user_email");
      localStorage.removeItem("abs_prepared_by");
      localStorage.removeItem("abs_prepared_by_email");
    }
    setEmail(v);
    setEditing(false);
    window.dispatchEvent(new Event("abs-user-changed"));
    toast.success(v ? (match ? `Signed in as ${match.name}` : "Email saved (add matching Account Manager to enable AM autofill)") : "Signed out");
  };

  return (
    <div className="flex items-center gap-2 px-1 text-xs" data-testid="profile-card">
      <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(email); if (e.key === "Escape") setEditing(false); }}
              placeholder="you@absinc.com"
              className="h-8 text-sm max-w-xs"
              data-testid="input-user-email"
            />
            <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800 text-white" onClick={() => save(email)} data-testid="btn-save-user-email">{tr("Save")}</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(false)}>{tr("Cancel")}</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-slate-400">{tr("Signed in as")}</span>
            {email ? (
              <span className="font-medium text-slate-800">
                {match ? <>{match.name} <span className="text-slate-400 font-normal">· {email}</span></> : email}
              </span>
            ) : (
              <span className="text-slate-400 italic">No user set</span>
            )}
            {email && !match && accountManagers.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="profile-no-am-match">
                Not an Account Manager — AM autofill disabled
              </span>
            )}
          </div>
        )}
      </div>
      {!editing && (
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => setEditing(true)} data-testid="btn-change-user-email">
            {email ? "Change" : "Sign in"}
          </Button>
          {email && (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400" onClick={() => save("")} data-testid="btn-sign-out">
              {tr("Sign out")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
